import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';

import LearnerMetrics from '../components/metrics/LearnerMetrics';
import AdminConsole from '../components/metrics/admin/AdminConsole';
import CohortPage from '../components/metrics/cohort/CohortPage';
import { ErrorBox } from '../components/metrics/MetricsUI';
import { setCurrentPage } from '../redux/app.slice';

/**
 * The Metrics page. One route, three shapes, chosen by role:
 *
 *   trainee -> their own progress
 *   leader  -> their organisation's people, drilling into any one of them
 *   admin   -> every organisation, then its people, then one person
 *
 * The role only decides what is OFFERED. Every request is authorised again on the
 * server from the verified token, so a trainee who reached a manager view could
 * still not read anyone else's data.
 */
/*
 * Where the old dashboards live, by role.
 *
 * This page is now the only route back to them: the "See Insights" button on
 * the home page and the "Insights" link in the header were both removed, so
 * without the banner below, anyone who still relies on the old view would have
 * no way to reach it.
 *
 * Mapped explicitly rather than built as `/${role}/dash`, because some accounts
 * carry roles the enum never had — 'orgLead' and 'OrgAdmin' appear in client
 * code — and those would navigate to a route that does not exist. A role with no
 * entry here gets no banner rather than a dead link.
 */
const OLD_DASHBOARD = {
  admin: '/admin/dash',
  leader: '/leader/dash',
  trainee: '/trainee/dash',
};

export default function Metrics() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.user, shallowEqual);
  const { role } = useSelector((state) => state.user.user, shallowEqual);
  // The org header names whoever is looking, so a manager can tell at a glance
  // whose permissions produced the page they are reading.
  const viewerInfo = useSelector((state) => state.user.user, shallowEqual);

  // Where we are in the nest. An admin starts at the org list; a leader starts
  // inside their own org, which the server picks from their token.
  const [openOrg, setOpenOrg] = useState(null);
  const [openLearner, setOpenLearner] = useState(null);

  useEffect(() => {
    dispatch(setCurrentPage('metrics'));
  }, [dispatch]);

  const idToken = user && (user.idToken || user.accessToken);
  const isAdmin = role === 'admin';
  const isCohortRole = role === 'leader' || isAdmin;

  if (!idToken) {
    return (
      <div className="min-h-screen bg-gray-100 p-6 font-metrics">
        <ErrorBox message="Not signed in." onRetry={() => navigate('/login')} />
      </div>
    );
  }

  const oldDashboard = OLD_DASHBOARD[role] || null;

  const subtitle = isAdmin
    ? 'Every organisation on the platform'
    : role === 'leader'
      ? 'Your organisation'
      : 'Your progress';

  return (
    <div className="min-h-screen bg-gray-100 p-6 font-metrics">
      <div className="mx-auto max-w-[1400px]">
        {/*
          * The route back to the old dashboards lives in the header, beside
          * Home, because that is what it is: navigation.
          *
          * It started as an amber banner across the top. That was wrong twice
          * over — amber is the colour this page uses for "a region did not
          * answer", so a permanent signpost was borrowing the vocabulary of a
          * problem; and a full-width box above the content implied the old view
          * matters more than the page you are on. A quiet link that sits where
          * every other navigation control sits is findable without being loud,
          * and it stays findable forever rather than being dismissed and lost.
          *
          * A real Link, not a navigate() button, so it can still be opened in a
          * new tab or bookmarked the way it used to be.
          */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Metrics</h1>
            <p className="text-sm text-gray-500">{subtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            {oldDashboard && (
              <Link
                to={oldDashboard}
                title="The previous Insights dashboard, unchanged"
                className="group inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-gray-500 transition-colors hover:bg-white hover:text-gray-700 hover:shadow-sm"
              >
                <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
                  <path
                    d="M10 5.5v4l2.5 1.5M17 10a7 7 0 11-2.05-4.95M17 3.5V7h-3.5"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Old insights view
              </Link>
            )}
            <button
              onClick={() => navigate('/')}
              className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm hover:shadow"
            >
              ← Home
            </button>
          </div>
        </div>

        {/* A trainee only ever sees themselves. */}
        {!isCohortRole && <LearnerMetrics idToken={idToken} />}

        {/* One person, opened from a roster. Same component the learner sees of
            themselves — the server decides whether the caller may read them. */}
        {isCohortRole && openLearner && (
          <LearnerMetrics
            idToken={idToken}
            targetUserId={openLearner.userId}
            targetUserIds={openLearner.userIds}
            onBack={() => setOpenLearner(null)}
            backLabel="Back to people"
          />
        )}

        {/* Admin, no org chosen yet: the whole platform. The console replaced the
            plain org list — its Accounts tab IS that list, with health beside it. */}
        {isAdmin && !openLearner && !openOrg && (
          <AdminConsole
            idToken={idToken}
            viewer={viewerInfo}
            onOpenOrg={(org) => org && setOpenOrg(org)}
          />
        )}

        {/* One organisation. A leader lands here directly with no organizationId:
            the server pins them to their own org from their token. */}
        {isCohortRole && !openLearner && (openOrg || !isAdmin) && (
          <CohortPage
            idToken={idToken}
            organizationId={openOrg ? openOrg.organizationId : undefined}
            organizationIds={openOrg ? openOrg.organizationIds : undefined}
            viewer={viewerInfo}
            onOpenLearner={(userId, label, userIds) => setOpenLearner({ userId, label, userIds })}
            onBack={isAdmin ? () => setOpenOrg(null) : undefined}
            backLabel="All organisations"
          />
        )}
      </div>
    </div>
  );
}
